<template>
  <div class="wrapper-x-40">
    <AdminStickyHeader>
      <template #title>
        {{ element ? $t('main.title_edit_element') + ":" : $t('main.title_add_element') + ":" }}
        {{ this.element_group && this.element_group.data.attributes.name }}
      </template>
      <template #actions>
        <Loading
          v-if="loadingSaveData"
          class="mb-2"
        />
        <div class="form-group flex flex-wrap items-center gap-3">
          <button
            @click="saveClose"
            type="button"
            class="btn btn-small btn-default"
          >
            {{ $t('main.button_save_close') }}
          </button>

          <button
            @click="save"
            type="button"
            class="btn btn-small btn-green"
          >
            {{ $t('main.button_save') }}
          </button>

          <button
            @click="saveCreate"
            type="button"
            class="btn btn-small btn-default"
          >
            <i
              class="fa fa-plus"
              aria-hidden="true"
            ></i>
            {{ $t('main.button_save_create') }}
          </button>

          <router-link
            class="btn btn-small btn-red"
            :to="{
              name: 'elements',
              params: {
                slug:
                  this.element_group && this.element_group.data.attributes.slug,
              },
              query: {...$route.query}
            }"
          >{{ $t('main.button_cancel') }}</router-link>

          <button
            @click="saveCopy"
            type="button"
            class="btn btn-small btn-blue"
          >
            {{ $t('main.button_save_copy') }}
          </button>

          <BaseButtonCopyLink
            v-if="element"
            :elementId="element.data.element_id"
            type="form"
          />
        </div>
      </template>
    </AdminStickyHeader>
    <div class="form">
      <div class="flex items-start">
        <div class="w-1/3">
          <div class="form-group">
            <label
              for="name"
              class="font-bold"
            >{{ $t('main.label_name') }} *:</label>
            <div class="flex items-center">
              <input
                id="name"
                type="text"
                v-model="form.name"
              />
            </div>
            <ValidationErrors :errors="errorsFor('name')" />
          </div>

          <div class="form-group">
            <label
              for="article"
              class="font-bold"
            >{{ $t('main.label_articul') }}</label>
            <div class="flex items-center">
              <input
                id="article"
                type="text"
                v-model="form.article"
              />
            </div>
            <ValidationErrors :errors="errorsFor('article')" />
          </div>

          <div
            class="form-group"
            v-if="types&&types.length"
          >
            <label
              for="type_id"
              class="font-bold"
            >{{ $t('main.select_type') }} *:</label>
            <div class="flex items-center">
              <select
                id="type_id"
                v-model="form.type_id"
              >
                <option :value="0">{{ $t('main.type_not_selected') }}</option>
                <option
                  v-for="type in types"
                  :key="type.data.type_id"
                  :value="type.data.type_id"
                >
                  {{ type.data.attributes.name }}
                </option>
              </select>
            </div>
            <ValidationErrors :errors="errorsFor('type_id')" />
          </div>
          <div class="form-group">
            <label class="font-bold">{{ $t('main.label_status') }}: </label>
            <div>
              <button
                class="rounded flex items-center focus:outline-none gap-2"
                type="button"
                @click="form.status = form.status == 1 ? 0 : 1"
              >
                <span class="check"><i
                    v-if="form.status == 1"
                    class="fas fa-check text-red-500"
                  ></i></span>
                <span class="text-sm">{{$t('main.published')}}</span>
              </button>
            </div>
          </div>

          <div class="form-group bg-lightblue-border">
            <div class="flex items-center">
              <label
                for="image"
                class="font-bold"
              >{{ $t('main.label_image') }}:
              </label>
              <div class="help ml-2 flex">
                <button class="
                    bg-gray-400
                    flex
                    justify-center
                    items-center
                    text-white
                    w-4
                    h-4
                    rounded-full
                    focus:outline-none
                  ">
                  ?
                </button>
              </div>
            </div>

            <FileInputBlock
              :prefill="imagePreview"
              :selectedImageFileNameDefault="$t('main.image_not_selected')"
              :alreadyLoadText="$t('main.image_selected')"
              :loadedFile="element&&element.data.attributes.image"
              @selected-file="(file) => selectFile(file, 'Image')"
              @remove-file="removeFile('Image')"
            >
              <ValidationErrors :errors="errorsFor('image')" />
            </FileInputBlock>
          </div>

          <div class="form-group bg-lightdarkblue-border">
            <div class="flex items-center">
              <label
                for="model3d"
                class="font-bold"
              >{{$t('admin.views.elements.3d_model_elements')}}*:
              </label>
              <div class="help ml-2 flex">
                <button class="
                    bg-red-700
                    flex
                    justify-center
                    items-center
                    text-white
                    w-4
                    h-4
                    rounded-full
                    focus:outline-none
                  ">
                  ?
                </button>
              </div>
            </div>
            <FileInputBlock
              :alreadyLoadText="$t('main.3d_model_selected')"
              :loadedFile="element&&element.data.attributes.model3d"
              :prefill="model3dPreview"
              :selectedImageFileNameDefault="$t('main.3d_model_not_selected')"
              @selected-file="(file) => selectFile(file, 'Model3d')"
              @remove-file="removeFile('Model3d')"
            >
              <ValidationErrors :errors="errorsFor('model3d')" />
            </FileInputBlock>
          </div>

          <div v-if="element_group.data.attributes.faces&&element_group.data.attributes.faces.length">

            <div class="font-bold mt-4 mb-4">
              {{ $t('admin.views.elements.face_to_engraving') }}:
            </div>

            <div
              class="mb-4"
              v-for="face in element_group.data.attributes.faces"
              :key="`face-${face.id}`"
            >
              <button
                class="focus:outline-none flex items-start justify-start text-left"
                @click="toggleFace(face)"
              >
                <span>
                  <span class="check"><i
                      v-if="form.faces.includes(face.id)"
                      class="fas fa-check text-red-500"
                    ></i></span>
                </span>

                <span class="ml-2 text-sm">
                  <span
                    class="text-red-500"
                    v-if="!face.status"
                  >[{{ $t('admin.views.elements.face_block') }}]</span>
                  {{ face.name }}

                </span>
              </button>
            </div>

          </div>

        </div>
        <div class="w-2/3 ml-30">
          <div class="form-group mb-8">
            <div class="font-bold mb-2">{{$t('main.material_list')}}:</div>
            <div
              class="overflow-x-hidden p-2 flex flex-wrap mb-4"
              style="height: 260px"
            >
              <material-list-card-in-element
                v-for="(material,i) in element_materials"
                :material="material"
                :element="true"
                :key="Math.random()*((i+Math.random())/100)"
              />

            </div>
            <div class="mb-8">
              <Modal
                v-if="addMaterialShowModal"
                @close-modal="addMaterialShowModal = false"
              >
                <div class="font-bold mb-30">{{$t('main.material')}}</div>
                <div
                  class="overflow-x-hidden p-2 flex flex-wrap"
                  style="height: 400px"
                >
                  <div
                    v-for="material in materials"
                    :key="`list-to-check-material-${material.data.material_id}`"
                  >
                    <material-list-card-to-check
                      :material="material"
                      :key="`to-check-material-${material.data.material_id}`"
                    />
                  </div>
                </div>
              </Modal>
              <button
                class="btn btn-default btn-small"
                @click="addMaterialShowModal = true"
              >
                {{$t('main.button_add_material')}}
              </button>
            </div>

            <div class="mb-8">
              <div class="font-bold mb-1">{{$t('main.calc_cost_element')}}:</div>

              <div class="flex items-center">
                <input
                  type="radio"
                  id="type_calc_cost_cub"
                  :checked="form.type_calc_cost == 'cub'"
                  v-model="form.type_calc_cost"
                  value="cub"
                />
                <label
                  for="type_calc_cost_cub"
                  class="ml-2 text-sm"
                  v-html="$t('admin.views.elements.type_calc_cost_cub')"
                ></label>
              </div>

              <div class="flex items-center">
                <input
                  type="radio"
                  id="type_calc_cost_coef"
                  :checked="form.type_calc_cost == 'coef'"
                  v-model="form.type_calc_cost"
                  value="coef"
                />
                <label
                  for="type_calc_cost_coef"
                  class="ml-2 text-sm"
                >{{$t('admin.views.elements.type_calc_cost_coef')}}</label>

              </div>

              <div class="flex items-center">
                <input
                  type="radio"
                  id="type_calc_cost_fix"
                  :checked="form.type_calc_cost == 'fix'"
                  v-model="form.type_calc_cost"
                  value="fix"
                />
                <label
                  for="type_calc_cost_fix"
                  class="ml-2 text-sm"
                >{{$t('admin.views.elements.type_calc_cost_fix')}}</label>
              </div>
            </div>

            <div v-if="form.type_calc_cost == 'cub'">
              <div class="font-bold mb-1">{{$t('main.calc_complexity')}}:</div>

              <div class="flex items-center">
                <input
                  type="radio"
                  id="type_calc_complexity_coef"
                  :checked="form.type_calc_complexity == 'coef'"
                  v-model="form.type_calc_complexity"
                  value="coef"
                />
                <label
                  for="type_calc_complexity_coef"
                  class="ml-2 text-sm"
                >{{$t('admin.views.elements.type_calc_complexity_coef')}}</label>
              </div>

              <div class="flex items-center">
                <input
                  type="radio"
                  id="type_calc_complexity_fix"
                  :checked="form.type_calc_complexity == 'fix'"
                  v-model="form.type_calc_complexity"
                  value="fix"
                />
                <label
                  for="type_calc_complexity_fix"
                  class="ml-2 text-sm"
                >{{$t('admin.views.elements.type_calc_complexity_fix')}}</label>
              </div>
            </div>

          </div>

          <div class="form-group">
            <div class="font-bold mb-2">{{$t('admin.views.elements.fact_size')}}:</div>
            <div class="element-sizes-list">
              <div
                class="mb-30 element-sizes-list-line"
                v-for="(size, index) in fact_sizes"
                :key="`element-size-${index}`"
              >
                <div class="flex items-end flex-wrap">
                  <div class="cm flex items-end mr-30 mb-4">
                    <div class="size-data-block height">
                      <label :for="`height-${index}`">{{ $t('main.height_sm') }}:</label>
                      <input
                        v-model="size.height"
                        type="number"
                        :id="`height-${index}`"
                        placeholder="000"
                        min="1"
                      />

                    </div>
                    <div class="x mb-1 ml-1 mr-1">X</div>
                    <div class="size-data-block width ml-1">
                      <label :for="`width-${index}`">{{ $t('main.width_sm') }}:</label>
                      <input
                        v-model="size.width"
                        type="number"
                        :id="`width-${index}`"
                        placeholder="000"
                        min="1"
                      />

                    </div>
                    <div class="x mb-1 ml-1">X</div>
                    <div class="size-data-block length ml-1">
                      <label :for="`length-${index}`">{{ $t('main.length_sm') }}:</label>
                      <input
                        type="number"
                        :id="`length-${index}`"
                        v-model="size.length"
                        placeholder="000"
                        min="1"
                      />

                    </div>

                  </div>

                </div>
                <div class="w-5/6">
                  <ValidationErrors :errors="errorsFor(`fact_sizes.${index}.height`)" />
                  <ValidationErrors :errors="errorsFor(`fact_sizes.${index}.width`)" />
                  <ValidationErrors :errors="errorsFor(`fact_sizes.${index}.length`)" />
                </div>

                <FaceDistances
                  :groupFacesProp="element_group.data.attributes.faces"
                  :formFacesProp="form.faces"
                  :sizeFaceDistancesProp="size.faceDistances"
                  :key="`el-fsize-${size.id}`"
                  :prefixLabel="`el-fsize-${size.id}`"
                  @changeDistances="(distances)=>onChangeDistance({
                        type: 'fact_size',
                        id: size.id,
                        distances
                    })"
                />

              </div>

            </div>

          </div>

          <div class="form-group">
            <div class="font-bold mb-2">{{$t('main.sizes')}} *:</div>

            <div class="element-sizes-list">
              <div
                class="flex mb-4 element-sizes-list-line"
                v-for="(size, index) in sizes"
                :key="`element-size-${index}`"
              >
                <div class="number-position mr-2 w-4">{{ index + 1 }}.</div>

                <div class="size-data flex flex-col pb-4">
                  <div class="flex flex-wrap">
                    <div class="cm flex items-end mr-30 mb-4">
                      <div class="size-data-block height">
                        <label :for="`height-${index}`">{{$t('main.height_sm')}}:</label>
                        <input
                          v-model="sizes[index].height"
                          type="number"
                          :id="`height-${index}`"
                          placeholder="000"
                        />
                      </div>
                      <div class="x mb-1 ml-1">X</div>
                      <div class="size-data-block width ml-1">
                        <label :for="`width-${index}`">{{$t('main.width_sm')}}:</label>
                        <input
                          v-model="sizes[index].width"
                          type="number"
                          :id="`width-${index}`"
                          placeholder="000"
                        />
                      </div>
                      <div class="x mb-1 ml-1">X</div>
                      <div class="size-data-block length ml-1">
                        <label :for="`length-${index}`">{{$t('main.length_sm')}}:</label>
                        <input
                          type="number"
                          :id="`length-${index}`"
                          v-model="sizes[index].length"
                          placeholder="000"
                        />
                      </div>
                    </div>
                    <div class="size-data-block weight mr-30 mb-4">
                      <label :for="`weight-${index}`">{{$t('main.weight_kg')}}:</label>
                      <input
                        type="number"
                        :id="`weight-${index}`"
                        placeholder="000"
                        v-model="sizes[index].weight"
                      />
                    </div>

                    <div
                      class="flex"
                      v-if="form.type_calc_cost=='cub'"
                    >
                      <div class="size-data-block cost-cub mr-30 mb-4">
                        <label
                          :for="`volume-${index}`"
                          v-html="$t('main.volume')"
                        ></label>
                        <input
                          type="number"
                          :id="`volume-${index}`"
                          placeholder="000"
                          v-model="sizes[index].volume"
                        />
                      </div>
                      <div class="size-data-block complexity mr-30 mb-4">
                        <label
                          :for="`complexity-${index}`"
                          v-html="$t('main.complexity')"
                        ></label>
                        <input
                          type="number"
                          :min="form.type_calc_complexity=='coef'?1:false"
                          :id="`complexity-${index}`"
                          placeholder="1"
                          v-model="sizes[index].complexity"
                        />
                      </div>
                    </div>
                    <div
                      v-else
                      class="flex gap-2"
                    >
                      <div class="size-data-block price mr-30 mb-4">
                        <label :for="`price-${index}`">{{$t('main.price')}}:</label>
                        <input
                          type="number"
                          :id="`price-${index}`"
                          placeholder="000"
                          v-model="sizes[index].price"
                        />
                      </div>
                      <div class="size-data-block price mr-30 mb-4 cost-value">
                        <label :for="`cost-${index}`">{{$t('main.label_cost_price')}}:</label>
                        <input
                          type="number"
                          :id="`cost-${index}`"
                          placeholder="000"
                          v-model="sizes[index].cost"
                        />
                      </div>
                    </div>

                    <div class="size-data-block coefficient mr-30 mb-4">
                      <label :for="`cost_coefficient-${index}`">{{$t('main.add_price_install')}}:</label>
                      <input
                        type="number"
                        :id="`cost_coefficient-${index}`"
                        placeholder="1"
                        v-model="sizes[index].cost_coefficient"
                      />
                    </div>

                  </div>
                  <FaceDistances
                    :groupFacesProp="element_group.data.attributes.faces"
                    :formFacesProp="form.faces"
                    :sizeFaceDistancesProp="size.faceDistances"
                    :key="`el-size-${size.id}`"
                    :prefixLabel="`el-size-${size.id}`"
                    @changeDistances="(distances)=>onChangeDistance({
                        type: 'size',
                        id: size.id,
                        distances
                    })"
                  />
                  <FileInputBlockSize
                    :alreadyLoadText="$t('main.model3d_size_loaded')"
                    :loadedFile="size.model3d"
                    :selectedImageFileNameDefault="$t('main.model3d_size_not_loaded')"
                    @selected-file="(file) => selectFileSize(file, size.id)"
                    @remove-file="removeFileSize(size.id)"
                  ></FileInputBlockSize>
                </div>

                <div class="size-remove pt-6">
                  <button
                    @click="removeSize(size.id)"
                    class="border-sky-blue px-2 rounded"
                    style="height: 36px"
                  >
                    <i class="fas fa-times fa-lg text-red-500"></i>
                  </button>
                </div>
              </div>
            </div>

            <div class="mb-30 flex">
              <div class="w-6"></div>
              <div>
                <button
                  class="btn btn-default btn-small"
                  @click="addSize"
                >
                  {{ $t('main.button_add_block_sizes') }}
                </button>
              </div>
            </div>

            <ValidationErrors :errors="errorsFor('sizes')" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MaterialListCard from "../../../components/Admin/Materials/MaterialListCard.vue";
import MaterialListCardInElement from "../../../components/Admin/Materials/MaterialListCardInElement.vue";
import MaterialListCardToCheck from "../../../components/Admin/Materials/MaterialListCardToCheck.vue";
import AdminStickyHeader from "../../../components/Admin/StickyHeader.vue";
import validationErrors from "../../../mixins/validationErrors";

import { v4 as uuidv4 } from "uuid";

const defaultImagePreview = "/images/design/zaglushka-prevyu.svg";
const defaultModel3dPreview = "/images/design/zaglushka-3d.svg";
const model3dUploaded = "/images/design/3d-model-uploaded.svg";
export default {
  components: {
    AdminStickyHeader,
    MaterialListCard,
    MaterialListCardInElement,
    MaterialListCardToCheck,
  },
  name: "Form",
  mixins: [validationErrors],
  props: {
    element: {
      type: Object,
    },
    element_group: {
      type: Object,
    },
  },
  data() {
    return {
      sizes: [],
      fact_sizes: [],
      addMaterialShowModal: false,
      imagePreview: this.element
        ? this.element.data.attributes.image || defaultImagePreview
        : defaultImagePreview,
      model3dPreview: this.element
        ? (this.element.data.attributes.model3d && model3dUploaded) ||
          defaultModel3dPreview
        : defaultModel3dPreview,
      loadingSaveData: false,
      form: {
        name: this.element ? this.element.data.attributes.name : "",
        article: this.element ? this.element.data.attributes.article : null,
        type_id: this.element ? this.element.data.attributes.type_id : null,
        status: this.element
          ? parseInt(this.element.data.attributes.status)
          : 1,
        faces:
          this.element &&
          this.element.data.attributes.faces &&
          this.element.data.attributes.faces.length
            ? this.element.data.attributes.faces
            : [],
        type_calc_cost: this.element
          ? this.element.data.attributes.type_calc_cost
          : "cub",
        type_calc_complexity: this.element
          ? this.element.data.attributes.type_calc_complexity
          : "coef",
        selectedModel3d: null,
        selectedImage: null,
        removeModel3d: false,
        removeImage: false,
      },
    };
  },
  mounted() {
    if (!this.licensed) this.$router.push({ name: "no_license" });
  },
  created() {
    this.hydrateFromProps();
  },
  computed: {
    types() {
      return this.$store.state.elements.types;
    },
    element_materials() {
      return this.$store.state.elements.element_materials;
    },
    materials() {
      return this.$store.state.materials.materials;
    },
    licensed() {
      return this.$store.getters["licensed"];
    },
  },
  watch: {
    element: {
      handler() {
        this.hydrateFromProps();
      },
      deep: false,
    },
  },
  methods: {
    hydrateFromProps() {
      const elementAttributes = this.element?.data?.attributes || null;

      this.imagePreview = elementAttributes?.image || defaultImagePreview;
      this.model3dPreview =
        (elementAttributes?.model3d && model3dUploaded) || defaultModel3dPreview;

      this.form = {
        name: elementAttributes?.name ?? "",
        article: elementAttributes?.article ?? null,
        type_id: elementAttributes?.type_id ?? null,
        status:
          elementAttributes?.status !== undefined &&
          elementAttributes?.status !== null
            ? parseInt(elementAttributes.status)
            : 1,
        faces:
          elementAttributes?.faces && elementAttributes.faces.length
            ? elementAttributes.faces
            : [],
        type_calc_cost: elementAttributes?.type_calc_cost ?? "cub",
        type_calc_complexity: elementAttributes?.type_calc_complexity ?? "coef",
        selectedModel3d: null,
        selectedImage: null,
        removeModel3d: false,
        removeImage: false,
      };

      this.sizes = this.$store.state.elements.element_sizes || [];
      if (this.sizes.length === 0) this.addSize();

      this.fact_sizes = elementAttributes?.fact_sizes || [];
      if (this.fact_sizes.length === 0) this.addFactSize();
    },
    async resetForCreate() {
      await this.$store.dispatch("elements/clearElementSizesAndMaterials");
      this.errors = null;
      this.status = null;
      this.hydrateFromProps();
    },
    async saveClose() {
      const element = await this.saveData();
      if (element) {
        this.$router.replace({
          name: "elements",
          params: {
            slug: this.element_group.data.attributes.slug,
          },
          query: { ...this.$route.query },
        });
      }
    },
    async save() {
      const element = await this.saveData();
      const elementId = element?.element?.data?.element_id;
      if (!elementId) return;
      const currentSlug = this.$route?.params?.slug;
      const currentElementId = this.$route?.params?.element_id;
      const targetSlug = this.element_group.data.attributes.slug;
      if (
        this.$route.name === "edit_element" &&
        String(currentSlug) === String(targetSlug) &&
        String(currentElementId) === String(elementId)
      ) {
        return;
      }
      await this.$router
        .replace({
          name: "edit_element",
          params: {
            slug: this.element_group.data.attributes.slug,
            element_id: elementId,
          },
          query: { ...this.$route.query },
        })
        .catch((error) => {});
    },
    async saveCreate() {
      const newElement = await this.saveData();
      if (newElement) {
        if (this.$route.name == "add_element") {
          await this.resetForCreate();
        } else {
          await this.$router
            .replace({
              name: "add_element",
              params: {
                slug: this.element_group.data.attributes.slug,
              },
              query: { ...this.$route.query },
            })
            .catch((error) => {});
        }
      }
    },
    async saveCopy() {
      const element = await this.saveData();

      if (element) {
        const elementId = element?.element?.data?.element_id;
        if (!elementId) return;
        const clone = await this.$store.dispatch(
          "elements/copySingle",
          elementId
        );

        await this.$router
          .replace({
            name: "edit_element",
            params: {
              slug: this.element_group.data.attributes.slug,
              element_id: clone.element.data.element_id,
            },
            query: { ...this.$route.query },
          })
          .catch((error) => {});
      }
    },
    toggleFace(face) {
      if (this.form.faces.includes(face.id)) {
        this.form.faces = this.form.faces.filter((f) => f != face.id);
      } else {
        this.form.faces.push(face.id);
      }
    },
    selectFile(file, dataProps) {
      this.form[`selected${dataProps}`] = file;
      this.form[`remove${dataProps}`] = false;
    },
    removeFile(dataProps) {
      this.form[`selected${dataProps}`] = null;
      this.form[`remove${dataProps}`] = true;
    },

    selectFileSize(file, sizeId) {
      const sizeIndex = this.sizes.findIndex((s) => s.id == sizeId);
      if (sizeIndex > -1) {
        this.sizes[sizeIndex].model3d = file;
        this.sizes[sizeIndex].removeModel3d = false;
      }
    },

    removeFileSize(sizeId) {
      const sizeIndex = this.sizes.findIndex((s) => s.id == sizeId);
      if (sizeIndex > -1) {
        this.sizes[sizeIndex].model3d = null;
        this.sizes[sizeIndex].removeModel3d = true;
      }
    },

    addSize() {
      this.sizes.push({
        id: uuidv4(),
        height: null,
        width: null,
        length: null,
        price: null,
        cost: null,
        cost_coefficient: null,
        volume: null,
        complexity: null,
        model3d: null,
        removeModel3d: false,
        faceDistances: [],
      });
    },
    removeSize(id) {
      this.sizes = this.sizes.filter((size) => size.id != id);
    },

    addFactSize() {
      this.fact_sizes.push({
        id: uuidv4(),
        height: null,
        depth: null,
        length: null,
        width: null,
        price: null,
        cost: null,
        faceDistances: [],
      });
    },
    onChangeDistance({ type, id, distances }) {
      let sizeIndex = null;

      if (type === "size") {
        sizeIndex = this.sizes.findIndex((s) => s.id == id);
      }
      if (type === "fact_size") {
        sizeIndex = this.fact_sizes.findIndex((s) => s.id == id);
      }

      if (sizeIndex >= 0) {
        if (type === "size") {
          this.sizes[sizeIndex]["faceDistances"] = distances;
        }
        if (type === "fact_size") {
          this.fact_sizes[sizeIndex]["faceDistances"] = distances;
        }
      }
    },

    async saveData() {
      this.errors = null;
      this.loadingSaveData = true;
      try {
        let formDataPost = new FormData();

        if (this.element) formDataPost.append("_method", "PATCH");

        formDataPost.append("name", this.form.name);
        formDataPost.append("article", this.form.article);
        formDataPost.append("type_id", this.form.type_id);
        formDataPost.append("status", parseInt(this.form.status));

        if (this.form.faces && this.form.faces.length) {
          for (const face_id of this.form.faces) {
            formDataPost.append("faces[]", face_id);
          }
        }
        formDataPost.append("type_calc_cost", this.form.type_calc_cost);

        formDataPost.append(
          "type_calc_complexity",
          this.form.type_calc_complexity
        );

        if (this.form.selectedImage !== null) {
          formDataPost.append("image", this.form.selectedImage);
        }

        if (this.form.selectedModel3d !== null) {
          formDataPost.append("model3d", this.form.selectedModel3d);
        }

        if (this.form.selectedFaceFrontImage !== null) {
          formDataPost.append(
            "front_face_image",
            this.form.selectedFaceFrontImage
          );
        }
        if (this.form.selectedFaceBackImage !== null) {
          formDataPost.append(
            "back_face_image",
            this.form.selectedFaceBackImage
          );
        }

        if (this.form.removeImage) {
          formDataPost.append("remove_image", this.form.removeImage);
        }

        if (this.form.removeModel3d) {
          formDataPost.append("remove_model3d", this.form.removeModel3d);
        }

        if (this.form.removeFaceFrontImage) {
          formDataPost.append(
            "remove_front_face_image",
            this.form.removeFaceFrontImage
          );
        }
        if (this.form.removeFaceBackImage) {
          formDataPost.append(
            "remove_back_face_image",
            this.form.removeFaceBackImage
          );
        }

        if (this.element_materials.length > 0) {
          this.element_materials.forEach((material) => {
            formDataPost.append("materials[]", material.data.material_id);
          });
        }

        if (this.sizes.length > 0) {
          this.sizes.forEach((size, index) => {
            formDataPost.append(`sizes[${index}][id]`, size.id);
            formDataPost.append(`sizes[${index}][height]`, size.height);
            formDataPost.append(`sizes[${index}][width]`, size.width);
            formDataPost.append(`sizes[${index}][length]`, size.length);
            formDataPost.append(`sizes[${index}][weight]`, size.weight);
            formDataPost.append(`sizes[${index}][price]`, size.price);
            formDataPost.append(`sizes[${index}][cost]`, size.cost);
            formDataPost.append(`sizes[${index}][volume]`, size.volume);
            formDataPost.append(`sizes[${index}][complexity]`, size.complexity);
            formDataPost.append(`sizes[${index}][model3d]`, size.model3d);

            if (size.removeModel3d) {
              formDataPost.append(
                `sizes[${index}][remove_model3d]`,
                size.removeModel3d
              );
            }

            if (size.faceDistances && size.faceDistances.length) {
              size.faceDistances
                .filter((fd) => fd?.show)
                .forEach((fd, fd_index) => {
                  formDataPost.append(
                    `sizes[${index}][face_distances][${fd_index}][face_id]`,
                    fd.face.id
                  );
                  formDataPost.append(
                    `sizes[${index}][face_distances][${fd_index}][x_active]`,
                    +fd.x_active
                  );
                  formDataPost.append(
                    `sizes[${index}][face_distances][${fd_index}][y_active]`,
                    +fd.y_active
                  );
                  formDataPost.append(
                    `sizes[${index}][face_distances][${fd_index}][z_active]`,
                    +fd.z_active
                  );
                  formDataPost.append(
                    `sizes[${index}][face_distances][${fd_index}][x_value]`,
                    parseFloat(fd.x_value)
                  );
                  formDataPost.append(
                    `sizes[${index}][face_distances][${fd_index}][y_value]`,
                    parseFloat(fd.y_value)
                  );
                  formDataPost.append(
                    `sizes[${index}][face_distances][${fd_index}][z_value]`,
                    parseFloat(fd.z_value)
                  );
                });
            }

            formDataPost.append(
              `sizes[${index}][cost_coefficient]`,
              size.cost_coefficient
            );
          });
        }

        if (this.fact_sizes.length > 0) {
          this.fact_sizes.forEach((size, index) => {
            formDataPost.append(`fact_sizes[${index}][id]`, size.id);
            if (size.height) {
              formDataPost.append(
                `fact_sizes[${index}][height]`,
                parseInt(size.height)
              );
            }
            if (size.depth) {
              formDataPost.append(
                `fact_sizes[${index}][depth]`,
                parseInt(size.depth)
              );
            }
            if (size.length) {
              formDataPost.append(
                `fact_sizes[${index}][length]`,
                parseInt(size.length)
              );
            }
            if (size.width) {
              formDataPost.append(
                `fact_sizes[${index}][width]`,
                parseInt(size.width)
              );
            }
            formDataPost.append(`fact_sizes[${index}][price]`, size.price);
            formDataPost.append(`fact_sizes[${index}][cost]`, size.cost);

            if (size.faceDistances && size.faceDistances.length) {
              size.faceDistances
                .filter((fd) => fd?.show)
                .forEach((fd, fd_index) => {
                  formDataPost.append(
                    `fact_sizes[${index}][face_distances][${fd_index}][face_id]`,
                    fd.face.id
                  );
                  formDataPost.append(
                    `fact_sizes[${index}][face_distances][${fd_index}][x_active]`,
                    +fd.x_active
                  );
                  formDataPost.append(
                    `fact_sizes[${index}][face_distances][${fd_index}][y_active]`,
                    +fd.y_active
                  );
                  formDataPost.append(
                    `fact_sizes[${index}][face_distances][${fd_index}][z_active]`,
                    +fd.z_active
                  );
                  formDataPost.append(
                    `fact_sizes[${index}][face_distances][${fd_index}][x_value]`,
                    parseFloat(fd.x_value)
                  );
                  formDataPost.append(
                    `fact_sizes[${index}][face_distances][${fd_index}][y_value]`,
                    parseFloat(fd.y_value)
                  );
                  formDataPost.append(
                    `fact_sizes[${index}][face_distances][${fd_index}][z_value]`,
                    parseFloat(fd.z_value)
                  );
                });
            }

            formDataPost.append(
              `fact_sizes[${index}][cost_coefficient]`,
              size.cost_coefficient
            );
          });
        }

        let url = `/api/admin/elements/${this.element_group.data.attributes.slug}`;
        if (this.element)
          url = `/api/admin/elements/${this.element_group.data.attributes.slug}/${this.element.data.element_id}`;

        const res = (
          await axios.post(url, formDataPost, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
        ).data;

        if (!res || typeof res !== "object" || !res.data || !res.data.element) {
          throw new Error("Некорректный ответ сервера");
        }

        return res.data;
      } catch (error) {
        this.errors = error?.response?.data?.errors || null;
        this.status = error?.response?.status || null;
        const notification = {
          type: "error",
          message:
            error?.response?.data?.message ||
            error?.message ||
            "Ошибка при сохранении",
        };
        this.$store.dispatch("notification/add", notification, {
          root: true,
        });
      } finally {
        this.loadingSaveData = false;
      }
    },
  },
};
</script>
